import {
  ONBOARDING_DISCORD_CHECK_ID,
  ONBOARDING_DISCORD_OAUTH_ID,
} from "testIds";
import React, { useState } from "react";
import styled from "styled-components";
import SubmitButton from "features/onboarding/common/SubmitButton";
import Text from "common/Typography/Text";
import { openMessage } from "utils/misc";
import Link from "common/Typography/Link";
import ConnectExternalAccountFooter from "features/onboarding/methodSettings/signalsGroup/connectExternalAccount/ConnectExternalAccountFooter";
import { ExternalAccountType } from "@shared/features/onboarding/types";
import CopyToClipboardInput from "common/CopyToClipboard/CopyToClipboardInput";
import useConnectDiscord from "@shared/features/onboarding/hooks/useConnectDiscord";
import useTypedTranslation from "@shared/common/i18n/useTypedTranslation";
import { LanguageNameSpaces } from "@shared/types";
import Button from "common/Buttons/Button";
import Divider from "common/misc/Divider";
import { Discord } from "common/Icons/Icons";
import { useStartDiscordLink } from "features/auth/useDiscordLink";

interface ConnectDiscordProps {
  onSuccess?: () => void;
}

const ConnectDiscord = ({ onSuccess }: ConnectDiscordProps) => {
  const { t } = useTypedTranslation(LanguageNameSpaces.ONBOARDING);
  const { onContinueClick, isFetching, discordBotLink, botLink, steps } =
    useConnectDiscord({
      openMessage,
      onSuccess,
    });
  const startDiscordLink = useStartDiscordLink();
  const [isRedirecting, setIsRedirecting] = useState(false);

  const onOAuthLinkClick = () => {
    setIsRedirecting(true);
    startDiscordLink().finally(() => setIsRedirecting(false));
  };

  const submitButton = (
    <SubmitButton
      data-testid={ONBOARDING_DISCORD_CHECK_ID}
      onClick={onContinueClick}
      loading={isFetching}
    >
      {t("external_account.discord.continue_button")}
    </SubmitButton>
  );

  return (
    <>
      <Button
        type="primary"
        data-testid={ONBOARDING_DISCORD_OAUTH_ID}
        $size="l"
        block
        loading={isRedirecting}
        icon={<Discord style={{ width: 20, height: 20 }} />}
        onClick={onOAuthLinkClick}
      >
        {t("external_account.discord.oauth_button")}
      </Button>
      <Hint type="secondary">{t("external_account.discord.oauth_hint")}</Hint>
      <Divider
        text={t("external_account.discord.manual_divider")}
        style={{ margin: "24px 0" }}
      />
      <Text style={{ marginBottom: 8 }}>
        {steps[0]}
        <Link href={discordBotLink} target="_blank" style={{ marginLeft: 4 }}>
          {t("external_account.discord.link_text")}
        </Link>
      </Text>
      <Text style={{ margin: "24px 0 16px 0" }}>{steps[1]}</Text>
      <CopyToClipboardInput
        text={botLink}
        notificationPrefix={t(
          "external_account.discord.copy_notification_prefix"
        )}
      />
      <Text as="div" style={{ marginTop: 24 }}>
        {steps[2]}
      </Text>
      <ConnectExternalAccountFooter
        submitButton={submitButton}
        externalAccountType={ExternalAccountType.DISCORD}
      />
    </>
  );
};

export default ConnectDiscord;

const Hint = styled(Text)`
  display: block;
  margin-top: 8px;
  text-align: center;
`;
